import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Badge, Button, CodeField, DefinitionList, Dialog, Drawer, Stack, t, Text, toast } from '@kanbios/ui';
import type { provisioning } from '@kanbios/api-client';
import { api, unwrap } from '../../lib/api';
import { useSession } from '../../lib/session';
import { PendingButton } from '../PendingButton';
import { explainAccess } from '../../lib/accessHint';
import { relativeTime, shortDateTime } from '../../lib/format';

type Candidate = provisioning.components['schemas']['DiscoveredServer'];
type Test = NonNullable<Candidate['test']>;
export type AccessOutcome = Test['outcome'];

export const ACCESS_TONES = { trusted: 'pos', no_sudo: 'warn', not_trusted: 'warn', unreachable: 'neg', not_applicable: 'neutral', unknown: 'neutral' } as const;
/** The cause in an Alert of this tone: a refusal to fix on the server (warn), nothing reaches it (neg). */
const OUTCOME_TONES: Record<AccessOutcome, 'pos' | 'warn' | 'neg'> = {
  trusted: 'pos', permission_denied: 'warn', no_sudo: 'warn', host_key_changed: 'warn', timeout: 'neg', refused: 'neg', no_route: 'neg', error: 'neg',
};

/** « 51.68.70.7:22 », « [2001:db8::1]:22 ». */
const endpoint = (address: string, port: number) => (address.includes(':') ? `[${address}]:${port}` : `${address}:${port}`);
const duration = (ms: number) =>
  (ms < 1000 ? t('access.detail.ms', { n: ms }) : t('access.detail.seconds', { n: (ms / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) }));

/** The one-line « Détail » of the access table: what the last check met. */
export function accessSummary(c: Candidate): string {
  if (c.test) return t(`access.detail.outcome.${c.test.outcome}`);
  if (c.access === 'trusted') return t('access.detail.outcome.trusted');
  return c.checkedAt ? explainAccess(c.detail) || t(`settings.access.states.${c.access}`) : t('import.access.notChecked');
}

/**
 * D59: what « Vérifier l'accès » tested on one server and what it met — the
 * address and port, the platform address it left from, when and how long,
 * the outcome with its cause and next step, the SSH message as it came;
 * for a server that trusts the platform, what the check read on it.
 * DSM Drawer (detail panel of a table row).
 */
export function AccessDetailDrawer({ server, onClose, onCheck, checking }: {
  server: Candidate | null;
  onClose: () => void;
  onCheck: (server: string) => void;
  checking: boolean;
}) {
  const { can } = useSession();
  const [confirmKey, setConfirmKey] = useState(false);
  const test = server?.test ?? null;
  // D76: a server reinstalled presents a new host key; an administrator who compared it accepts it, then it is checked.
  const presented = test?.outcome === 'host_key_changed' ? test.presentedKey ?? null : null;
  const accept = useMutation({
    mutationFn: async ({ name, fingerprint }: { name: string; fingerprint: string }) =>
      unwrap(await api.serverAccess.POST('/servers/{server}/host-key/accept', { params: { path: { server: name } }, body: { fingerprint } })),
    onSuccess: (_r, { name }) => {
      toast({ title: t('access.detail.hostKey.accepted', { server: name }), tone: 'info' });
      onCheck(name);
    },
  });
  const vars: Record<string, string | number> = test ? { port: test.port, from: test.from ?? t('access.detail.platform'), address: test.address } : {};
  const firewall = server?.hostFirewall ?? null;
  const at = test?.startedAt ?? server?.checkedAt ?? null;
  return (
    <Drawer
      open={Boolean(server)}
      onClose={onClose}
      size="xl"
      eyebrow={t('access.detail.eyebrow')}
      title={server?.server ?? ''}
      subtitle={server ? <Badge tone={ACCESS_TONES[server.access]}>{t(`settings.access.states.${server.access}`)}</Badge> : undefined}
      footer={server ? (
        <>
          <Button variant="secondary" onClick={onClose}>{t('access.detail.close')}</Button>
          <PendingButton icon="refresh" pending={checking} onClick={() => onCheck(server.server)}>{t('access.detail.check')}</PendingButton>
        </>
      ) : undefined}
    >
      {server && (!test ? (
        <Stack gap={16}>
          <Alert tone="info" title={t('access.detail.notTested')}>{t('access.detail.notTestedBody')}</Alert>
          {server.detail && server.access !== 'trusted' && (
            <CodeField label={t('access.detail.message')} readOnly wrap lineNumbers={false} rows={2} value={server.detail} />
          )}
        </Stack>
      ) : (
        <Stack gap={24}>
          <DefinitionList
            items={[
              { key: 'address', label: t('access.detail.address'), value: endpoint(test.address, test.port), mono: true },
              { key: 'from', label: t('access.detail.from'), value: test.from ?? t('access.detail.unknownFrom'), mono: Boolean(test.from) },
              { key: 'at', label: t('access.detail.checkedAt'), value: at ? `${shortDateTime(at)} (${relativeTime(at)})` : null },
              { key: 'duration', label: t('access.detail.duration'), value: test.durationMs === null ? null : duration(test.durationMs) },
              { key: 'result', label: t('access.detail.result'), value: t(`access.detail.outcome.${test.outcome}`) },
            ]}
          />
          <Alert
            tone={OUTCOME_TONES[test.outcome]}
            title={t(`access.detail.cause.${test.outcome}`, vars)}
            action={presented && can('access.manage') ? t('access.detail.hostKey.accept') : undefined}
            onAction={() => setConfirmKey(true)}
          >
            {t(`access.detail.next.${test.outcome}`, vars)}
          </Alert>
          {presented && (
            <DefinitionList
              items={[{ key: 'presented', label: t('access.detail.hostKey.presented', { type: presented.type }), value: presented.fingerprint, mono: true }]}
            />
          )}
          {test.message && (
            <CodeField label={t('access.detail.message')} readOnly wrap lineNumbers={false} rows={3} maxHeight={240} value={test.message} />
          )}
          {test.outcome === 'trusted' && (
            <Stack gap={12}>
              <Text variant="card-title" as="h3">{t('access.detail.read')}</Text>
              <DefinitionList
                items={[
                  {
                    key: 'listening', label: t('access.detail.listening'),
                    value: !server.listening ? t('access.detail.notRead')
                      : server.listening.length === 0 ? t('access.detail.noListening')
                        : server.listening.map(l => `${l.port}/${l.proto}${l.processes.length ? ` (${l.processes.join(', ')})` : ''}`).join(', '),
                  },
                  { key: 'firewall', label: t('access.detail.firewall'), value: firewall ? t(`access.detail.ufw.${firewall.ufw}`, { n: firewall.rules.length }) : t('access.detail.notRead') },
                ]}
              />
              {firewall && firewall.rules.length > 0 && (
                <CodeField label={t('access.detail.ufwRules')} readOnly lineNumbers={false} copyable={false} rows={Math.min(6, firewall.rules.length)} value={firewall.rules.join('\n')} />
              )}
            </Stack>
          )}
        </Stack>
      ))}
      {server && presented && (
        <Dialog
          open={confirmKey}
          onClose={() => setConfirmKey(false)}
          tone="danger"
          size="md"
          title={t('access.detail.hostKey.dialogTitle', { server: server.server })}
          description={t('access.detail.hostKey.dialogBody', { address: test!.address })}
          confirmLabel={t('access.detail.hostKey.accept')}
          onConfirm={() => { setConfirmKey(false); accept.mutate({ name: server.server, fingerprint: presented.fingerprint }); }}
        >
          <Stack gap={16}>
            <DefinitionList items={[{ key: 'fp', label: t('access.detail.hostKey.presented', { type: presented.type }), value: presented.fingerprint, mono: true }]} />
            <CodeField
              label={t('access.detail.hostKey.command')}
              readOnly
              lineNumbers={false}
              rows={1}
              value={`ssh-keygen -lf /etc/ssh/ssh_host_${presented.type.toLowerCase()}_key.pub`}
            />
          </Stack>
        </Dialog>
      )}
    </Drawer>
  );
}
